import { styled } from 'styled-components';
import React, { useContext, useState } from 'react';

import { Button } from '@vivaahealth/design-system';

import * as api from '../../../../../../api';

import { PAYMENT_METHOD_TO_DISPLAY } from './common';
import { ConversationContext } from '../../../../../../context/conversationContext';
import {
  PaymentMethod,
  PaymentSelectMethodMessage,
} from '../../../../../../common/payment';
import MessageContent from '../../messageContent';

interface Props {
  message: PaymentSelectMethodMessage;
  showTextAnimation: boolean;
}

type PaymentOption = {
  method: PaymentMethod;
  id: string;
  disabled?: boolean;
};

const TOTAL_AMOUNT = 10000;

const PaymentSelection: React.FC<Props> = ({ message, showTextAnimation }) => {
  const [selectedMethod, setSelectedMethod] = React.useState<PaymentMethod>(
    PaymentMethod.SavedCreditCard,
  );
  const [isPartialPay, setIsPartialPay] = useState(false);
  const [partialAmount, setPartialAmount] = useState<number | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const conversationContext = useContext(ConversationContext);

  const paymentOptions: PaymentOption[] = [
    {
      method: PaymentMethod.SavedCreditCard,
      id: 'saved-credit-card',
    },
    {
      method: PaymentMethod.ApplePay,
      id: 'apple-pay',
    },
    {
      method: PaymentMethod.Paypal,
      id: 'paypal',
    },
    {
      method: PaymentMethod.NewCreditCard,
      id: 'new-credit-card',
      disabled: true,
    },
  ];

  const totalAmount = (TOTAL_AMOUNT / 100).toFixed(2);

  const handleMakePayment = () => {
    if (!conversationContext) return;
    setIsSubmitting(true);
    setTimeout(() => {
      const amountPaid =
        isPartialPay && partialAmount ? partialAmount * 100 : TOTAL_AMOUNT;
      conversationContext.disableMessages();
      conversationContext.updateCurrentMessage({
        ...message,
        intent: api.Assistant.Intent.Payment,
        intentComponent: api.Assistant.IntentComponent.PaymentConfirmed,
        answer:
          'Thank you for your payment. Below is your receipt.\nIs there anything else I can help you with?',
        done: true,
        data: {
          amountPaid,
          receipt: '63891287621',
          datePaid: new Date(),
          method: selectedMethod,
        },
      });
      setIsSubmitting(false);
    }, 1200);
  };

  return (
    <>
      <MessageContent
        content={message.answer}
        showTextAnimation={showTextAnimation}
        isAgent={'answer' in message}
        isLoading={!message.done}
      />
      <SelectPaymentWrapper data-test-id="payment-select-method">
        {paymentOptions.map((option) => {
          return (
            <PaymentMethodRow key={option.method}>
              <RadioButton
                type="radio"
                checked={selectedMethod === option.method}
                onChange={() => setSelectedMethod(option.method)}
                id={option.id}
                disabled={option.disabled || message.disabled || isSubmitting}
              />
              <label htmlFor={option.id}>
                {PAYMENT_METHOD_TO_DISPLAY[option.method]}
              </label>
            </PaymentMethodRow>
          );
        })}
      </SelectPaymentWrapper>
      <SelectPaymentWrapper>
        <Header>
          <span>Total balance due</span>
          <span>${totalAmount}</span>
        </Header>
        <PartialPayWrapper>
          <div>
            <input
              id="partial-pay"
              type="checkbox"
              className="form-check-input"
              value={isPartialPay ? 'checked' : ''}
              onChange={() => {
                setIsPartialPay((p) => !p);
                setPartialAmount(undefined);
              }}
              disabled={message.disabled || isSubmitting}
            />
            <PartialPayLabel htmlFor="partial-pay">Pay partial</PartialPayLabel>
          </div>
          {isPartialPay && (
            <div>
              <PartialPayInput
                className="form-control"
                placeholder={`$${totalAmount}`}
                value={partialAmount}
                type="number"
                min={0}
                disabled={message.disabled || isSubmitting}
                max={TOTAL_AMOUNT / 100}
                onChange={(e) => {
                  const value = +e.target.value;
                  const totalInDollars = TOTAL_AMOUNT / 100;
                  if (value < 0) {
                    setPartialAmount(0);
                  } else if (value > totalInDollars) {
                    setPartialAmount(totalInDollars);
                  } else {
                    setPartialAmount(value);
                  }
                }}
              />
            </div>
          )}
        </PartialPayWrapper>
      </SelectPaymentWrapper>
      <SubmitButton
        data-test-id="payment-select-method-submit"
        onClick={() => handleMakePayment()}
        disabled={
          (isPartialPay && !partialAmount) || message.disabled || isSubmitting
        }
      >
        {isPartialPay
          ? `Pay partial: $${partialAmount ?? '0'}`
          : `Pay total due: $${totalAmount}`}
      </SubmitButton>
    </>
  );
};

const SelectPaymentWrapper = styled.div`
  margin-top: 20px;
  border: 1px solid var(--border-color);
  font-size: 15px;
`;

const Header = styled.div`
  background-color: var(--color-shade-30);
  display: flex;
  justify-content: space-between;
  padding: 5px 15px;
  font-size: 16px;
  font-weight: 500;
`;

const PartialPayInput = styled.input`
  margin-top: 10px;
`;

const PartialPayWrapper = styled.div`
  padding: 10px 15px;
`;

const PaymentMethodRow = styled.div`
  padding: 10px 15px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid var(--border-color);

  &:last-child {
    border-bottom: none;
  }
`;

const RadioButton = styled.input`
  margin-right: 15px;
  height: 20px;
  width: 20px;
`;

const SubmitButton = styled(Button)`
  width: 100%;
  margin-top: 20px !important;
`;

const PartialPayLabel = styled.label`
  font-size: 15px;
  margin-left: 15px;
`;

export default PaymentSelection;
